import { useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import { PageHeader } from "../../components/PageHeader";
import { DeployWizard } from "../deploy-wizard/DeployWizard";
import { RECIPES } from "./catalog";
import "./gallery.css";

/**
 * Gallery v0 (M2 PR-4, #245; docs/product/personas.md "Scaffolds"): curated
 * stack recipes built on the existing compose-import flow — "how do I build
 * X?" answered with a working start, not a blank project.
 *
 * "Deploy this stack" opens the `DeployWizard` — a guided multi-step modal
 * that chains project creation, compose import, placement, secret creation,
 * and deploy into one flow. The wizard replaces the old sessionStorage hop
 * through `CreateProjectModal` → `ImportComposeModal` that created service
 * records but never actually deployed anything (an A-10 lying surface: the
 * button said "Deploy" but the result was an empty project with service
 * definitions and zero running containers).
 */
export function GalleryPage() {
  const { session } = useAuth();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardRecipeId, setWizardRecipeId] = useState<string | undefined>();

  // Mirrors the server's authz matrix (project:create is admin/operator
  // only, server/internal/authz/model.go) — hides the control only; the
  // server enforces the real decision on every request (S-8). A viewer can
  // still browse recipes and preview their compose — deploying is the write.
  const canManage = canManageFleet(session?.role);

  function handleDeploy(recipeId: string) {
    setWizardRecipeId(recipeId);
    setWizardOpen(true);
  }

  return (
    <div className="gallery">
      <PageHeader
        title="Gallery"
        description="Curated stack recipes — pick one to start from a pre-built stack, then give it somewhere to run."
      />

      <div className="gallery__grid">
        {RECIPES.map((recipe) => {
          const headingId = `gallery-recipe-${recipe.id}`;
          return (
            <article className="gallery__card" key={recipe.id} aria-labelledby={headingId}>
              <h2 id={headingId} className="gallery__card-title">
                {recipe.name}
              </h2>
              <p className="gallery__card-description">{recipe.description}</p>

              {recipe.tags.length > 0 && (
                <ul className="gallery__tags" aria-label={`${recipe.name} tags`}>
                  {recipe.tags.map((tag) => (
                    <li key={tag} className="gallery__tag">
                      {tag}
                    </li>
                  ))}
                </ul>
              )}

              {recipe.secrets && recipe.secrets.length > 0 && (
                <p className="gallery__secrets-note">
                  Needs secrets you create yourself: {recipe.secrets.map((s) => s.name).join(", ")}. The compose
                  below only references them by name — no values are stored here.
                </p>
              )}

              <details className="gallery__compose-preview">
                <summary>Preview compose.yml</summary>
                <pre>{recipe.compose}</pre>
              </details>

              {canManage ? (
                <button
                  type="button"
                  className="button button--primary"
                  // No fleet-id resolution to wait on before stashing the pending recipe
                  // and creating the project: ADR-0035 M1 PR-D deleted FleetContext.tsx —
                  // tenant comes solely from the session now, already resolved by the time
                  // this page renders.
                  onClick={() => handleDeploy(recipe.id)}
                >
                  Deploy this stack
                </button>
              ) : (
                <p className="gallery__hint">Ask an admin or operator to deploy this stack.</p>
              )}
            </article>
          );
        })}
      </div>

      {canManage && (
        <DeployWizard
          key={wizardRecipeId ?? ""}
          open={wizardOpen}
          onClose={() => {
            setWizardOpen(false);
            setWizardRecipeId(undefined);
          }}
          initialRecipeId={wizardRecipeId}
        />
      )}
    </div>
  );
}
